// src/InstallAppCard.js
// "Install the app" guide on Home. Picks the right steps for the phone and
// browser the family is using, because each one hides "Add to Home Screen" in
// a different place — and on iPhones it matters most: Safari forgets a website
// (and logs the family out) after about a week without a visit, but an app on
// the home screen keeps them logged in.
import React, { useEffect, useState } from "react";
import { ChevronDown, Download, EllipsisVertical, Menu, Share, SquarePlus } from "lucide-react";
import { cn } from "./lib/utils";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";

const HIDE_KEY = "dance_booking_install_hidden";

// Chrome offers its install prompt once, when the app first loads — often
// before Home is on screen — so it's caught here, as soon as this file loads.
let deferredPrompt = null;
const promptListeners = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    promptListeners.forEach((fn) => fn(e));
  });
}

export function isInstalled() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

// Which set of steps to show.
export function detectPlatform() {
  const ua = window.navigator.userAgent || "";
  const ios = /iphone|ipad|ipod/i.test(ua) || (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);
  const android = /android/i.test(ua);
  // Apps that open links inside themselves can't install a web app.
  const inApp = /FBAN|FBAV|FB_IAB|Instagram|Line\/|WhatsApp|Snapchat|TikTok|MicroMessenger/i.test(ua) || (android && /; wv\)/.test(ua));
  if (ios) {
    if (inApp) return "ios-inapp";
    if (/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)) return "ios-other";
    return "ios-safari";
  }
  if (android) {
    if (inApp) return "android-inapp";
    if (/SamsungBrowser/i.test(ua)) return "android-samsung";
    return "android";
  }
  return "desktop";
}

const Icon = ({ as: As }) => (
  <As className="mx-0.5 inline h-4 w-4 align-text-bottom text-accent" aria-hidden="true" />
);

// Steps per platform. Kept short: one action per step, naming the exact button.
const STEPS = {
  "ios-safari": [
    // Newer Safari tucks Share under the ••• button next to the address bar.
    <>Tap the <strong>•••</strong> button next to the address bar, then <strong>Share</strong> <Icon as={Share} />. (On older iPhones the <strong>Share</strong> button — a square with an arrow — is right at the bottom of the screen; on an iPad it's at the top.)</>,
    <>Scroll down and tap <strong>Add to Home Screen</strong> <Icon as={SquarePlus} />. If you don't see it, tap <strong>Edit Actions</strong> at the very bottom and add it.</>,
    <>Tap <strong>Add</strong> in the top corner.</>,
    <>Open AuthenTrix from the new icon on your home screen — not from Safari — and log in once with your email and code.</>,
  ],
  "ios-other": [
    <>Tap the <strong>Share</strong> button <Icon as={Share} /> next to the address bar.</>,
    <>Tap <strong>Add to Home Screen</strong> <Icon as={SquarePlus} />. If it isn't there, open this page in <strong>Safari</strong> instead and follow the steps there.</>,
    <>Tap <strong>Add</strong>, then open AuthenTrix from the new icon on your home screen.</>,
  ],
  "ios-inapp": [
    <>You've opened this inside another app (like WhatsApp or Facebook), which can't install it.</>,
    <>Tap the <strong>Share</strong> <Icon as={Share} /> or <strong>⋯</strong> button and choose <strong>Open in Safari</strong> (or copy the link <strong>app.authentrix.co.za</strong> into Safari).</>,
    <>In Safari, tap <strong>Share</strong> <Icon as={Share} />, then <strong>Add to Home Screen</strong> <Icon as={SquarePlus} />, then <strong>Add</strong>.</>,
  ],
  android: [
    // Links opened from WhatsApp/Messenger often show in a mini Chrome window
    // (✕ at the top-left) that looks like Chrome but can't install.
    <>If there's an <strong>✕</strong> at the top-left (you opened the link from WhatsApp or Messenger), tap the <strong>⋮ menu</strong> <Icon as={EllipsisVertical} /> and choose <strong>Open in Chrome</strong> first.</>,
    <>Tap the <strong>⋮ menu</strong> <Icon as={EllipsisVertical} /> in the top-right corner of Chrome.</>,
    <>Tap <strong>Install app</strong> <Icon as={Download} /> (on some phones it's called <strong>Add to Home screen</strong>).</>,
    <>Tap <strong>Install</strong>, then open AuthenTrix from your home screen or app list.</>,
  ],
  "android-samsung": [
    <>Tap the <strong>menu</strong> <Icon as={Menu} /> at the bottom-right of Samsung Internet.</>,
    <>Tap <strong>Add page to</strong>, then <strong>Home screen</strong> (or <strong>Install app</strong> if you see it).</>,
    <>Open AuthenTrix from the new icon on your home screen.</>,
  ],
  "android-inapp": [
    <>You've opened this inside another app (like WhatsApp or Facebook), which can't install it.</>,
    <>Tap the <strong>⋮ menu</strong> <Icon as={EllipsisVertical} /> in the top-right corner and choose <strong>Open in Chrome</strong> (or copy the link <strong>app.authentrix.co.za</strong> into Chrome).</>,
    <>In Chrome, tap <strong>⋮</strong> <Icon as={EllipsisVertical} />, then <strong>Install app</strong> <Icon as={Download} />, then <strong>Install</strong>.</>,
  ],
  desktop: [
    <>On a computer, look for the install icon <Icon as={Download} /> at the right of the address bar (Chrome or Edge), or use the browser menu → <strong>Install AuthenTrix</strong>.</>,
    <>For the best experience, install it on your phone as well — open <strong>app.authentrix.co.za</strong> on your phone to see the steps.</>,
  ],
};

export default function InstallAppCard() {
  const [installPrompt, setInstallPrompt] = useState(() => deferredPrompt);
  const [installed, setInstalled] = useState(isInstalled);
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try { return !!localStorage.getItem(HIDE_KEY); } catch { return false; }
  });
  const platform = detectPlatform();
  const isIOS = platform.startsWith("ios");

  useEffect(() => {
    // Chrome/Edge on Android and computers can show their own install prompt.
    const onPrompt = (e) => setInstallPrompt(e);
    const onInstalled = () => setInstalled(true);
    promptListeners.add(onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      promptListeners.delete(onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;
  // iPhone users can't hide it — staying logged in depends on it.
  if (hidden && !isIOS) return null;

  const install = async () => {
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    deferredPrompt = null; // a prompt can only be shown once
    setInstallPrompt(null);
    if (outcome === "accepted") setInstalled(true);
  };

  const hide = () => {
    try { localStorage.setItem(HIDE_KEY, "1"); } catch { /* ignore */ }
    setHidden(true);
  };

  return (
    <Card className="mb-4 border-[var(--color-accent)]">
      <CardContent className="p-3">
        <div className="text-base font-bold text-foreground">📲 Install the AuthenTrix app</div>
        <p className="m-0 mt-1 text-sm text-muted-foreground">
          {isIOS
            ? <>On iPhone and iPad, <strong className="text-foreground">Safari logs you out after about a week</strong> if you don't open the site. Installing the app on your home screen keeps you logged in — it only takes a few seconds.</>
            : <>Get AuthenTrix on your home screen like any other app — quicker to open, and you stay logged in.</>}
        </p>

        {installPrompt ? (
          <Button className="mt-3 w-full" onClick={install}>Install app</Button>
        ) : (
          <>
            <Button variant={open ? "outline" : "default"} className="mt-3 w-full" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
              {open ? "Hide the steps" : "Show me how"}
              <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} aria-hidden="true" />
            </Button>
            {open && (
              <ol className="m-0 mt-3 flex flex-col gap-2 pl-5 text-sm text-foreground">
                {STEPS[platform].map((step, i) => <li key={i} className="pl-1 leading-relaxed">{step}</li>)}
              </ol>
            )}
          </>
        )}

        {!isIOS && (
          <button type="button" onClick={hide} className="mt-2 cursor-pointer border-0 bg-transparent p-0 text-xs text-muted-foreground underline [font-family:inherit]">
            Don't show this again
          </button>
        )}
      </CardContent>
    </Card>
  );
}
